import { useEffect, useState } from "react";
import { Box } from "@mui/material";
import { Outlet, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "./rtk/_config/hooks";
import { RootState } from "./types/rtk/api-config/types";
import { setAuth } from "./rtk/auth/auth";
import { exchangeDelegationToken } from "./rtk/auth/delegateExchange";
import SpinnerLoader from "./components/atoms/SpinnerLoader/SpinnerLoader";
import { useGetTrackerStateQuery } from "./rtk/tracker/tracker";
import { useNavigateWithParams } from "./hooks/useNavigateWithParams";
import { isAppSource } from "./helpers/getSourceFromUrl";

const App = () => {
  const navigate = useNavigateWithParams();
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [isAuthProcessed, setIsAuthProcessed] = useState(false);

  const auth_token = searchParams.get("auth");
  const delegationToken = searchParams.get("token");
  const trackerIdParam = searchParams.get("tracker_id");
  const lang = searchParams.get("lang");

  // Parse tracker_id defensively: a non-numeric value (e.g. "undefined" or a
  // truncated launch URL) would make Number() yield NaN, which later renders as
  // a literal "/tracker/NaN" path and 422s. Keep undefined unless it's a real int.
  const parsedTrackerId = trackerIdParam ? Number(trackerIdParam) : undefined;
  const trackerId = Number.isInteger(parsedTrackerId) ? parsedTrackerId : undefined;
  const external_id = useAppSelector((state: RootState) => state.auth.external_id);
  const storedDelegationToken = useAppSelector((state: RootState) => state.auth.delegation_token);
  const storedToken = useAppSelector((state: RootState) => state.auth.token);

  // Hold the tracker query until a credential is actually in store. `isAuthProcessed`
  // alone is not enough: it also flips on the branches below that mark the render
  // unblocked without having (re)set a credential on that pass, so the query would
  // fire unauthenticated — and, lacking a token, ship the unscoped legacy path.
  const hasCredential = Boolean(storedToken) || Boolean(external_id);
  const { data: trackerData } = useGetTrackerStateQuery(undefined, {
    skip: !isAuthProcessed || !hasCredential,
  });

  // Clean up auth URL query parameters once auth has been processed.
  const stripAuthParams = () => {
    const newSearchParams = new URLSearchParams(window.location.search);
    newSearchParams.delete("auth");
    newSearchParams.delete("token");
    newSearchParams.delete("tracker_id");
    const newSearch = newSearchParams.toString();
    const newPath = `${window.location.pathname}${newSearch ? `?${newSearch}` : ""}`;
    navigate(newPath, { replace: true });
  };

  // Handle authentication from URL params and clean up. The apiv5 path carries a
  // delegation token (`?token=`) — the multi-use mailtoken reused from the mobile
  // launch URL, or a token minted by user-space via /auth/delegate/create for web
  // sessions. We store the delegation token as the DURABLE credential and exchange
  // it for a short-lived (refresh-less) access token; the access token is
  // re-exchanged on demand by the API layer when it expires. The legacy path
  // carries a raw external_id (`?auth=`).
  useEffect(() => {
    let cancelled = false;

    const processAuth = async () => {
      if (delegationToken && delegationToken !== storedDelegationToken) {
        // apiv5: persist the new delegation token (durable) + tracker_id, then
        // exchange it for an initial access token. Crucially, CLEAR any previously
        // persisted access token: it may belong to a different user/session (a
        // stale token would be sent on the first request before the exchange
        // completes, yielding a 403 — which, unlike a 401, does not trigger a
        // re-exchange). Clearing it forces a fresh lazy exchange in the API layer.
        // Only carry tracker_id when the launch URL actually supplied one: the
        // reducer Object.assigns the payload, so passing undefined would wipe a
        // valid stored id. Some launch URLs legitimately omit it (the mobile
        // `manage-payments` entry point), and without an id every request falls
        // back to the unscoped legacy path even with a valid token.
        dispatch(
          setAuth({
            delegation_token: delegationToken,
            token: undefined,
            ...(trackerId !== undefined ? { tracker_id: trackerId } : {}),
          })
        );
        try {
          const { access_token } = await exchangeDelegationToken(delegationToken);
          if (cancelled) return;
          dispatch(setAuth({ token: access_token }));
        } catch {
          // Exchange failed (expired/used/revoked token); render without a token so
          // downstream calls surface the auth error rather than hanging on a spinner.
        }
        if (cancelled) return;
        stripAuthParams();
        setIsAuthProcessed(true);
      } else if (auth_token && external_id !== auth_token) {
        // Legacy external_id.
        dispatch(setAuth({ external_id: auth_token }));
        stripAuthParams();
        setIsAuthProcessed(true);
      } else {
        // Auth already set, or none in the URL. The access token is deliberately
        // never persisted (see the auth transform in rtk/_config/store.ts) while
        // the delegation token is, so a reload of an established session lands
        // here with a durable credential but no token. Re-derive one before we
        // unblock, otherwise the first request goes out unauthenticated — which
        // also means unscoped, on the legacy path.
        if (!storedToken && storedDelegationToken && !external_id) {
          try {
            const { access_token } = await exchangeDelegationToken(
              storedDelegationToken
            );
            if (cancelled) return;
            dispatch(setAuth({ token: access_token }));
          } catch {
            // Exchange failed (expired/used/revoked); fall through and render so
            // downstream calls surface the auth error rather than spinning.
          }
          if (cancelled) return;
        }
        if (auth_token || delegationToken) stripAuthParams();
        setIsAuthProcessed(true);
      }
    };

    processAuth();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth_token, delegationToken, trackerIdParam, external_id, storedDelegationToken, storedToken, dispatch]);

  // Handle language selection from URL params or user preferences
  // When source=app, i18next LanguageDetector already uses the browser language
  useEffect(() => {
    if (!isAppSource()) {
      const final_lang = lang || trackerData?.language || "en";
      if (final_lang && final_lang !== i18n.language) {
        i18n.changeLanguage(final_lang);
      }
    }
  }, [lang, trackerData?.language, i18n, dispatch]);

  // Wait for auth to be processed before rendering children
  if (!isAuthProcessed) {
    return (
      <Box className="App" display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <SpinnerLoader size={100} />
      </Box>
    );
  }

  return (
    <Box className="App">
      <Outlet />
    </Box>
  );
};

export default App;
